// src/components/ThreeViewer.jsx
//
// FIXES (May 2026):
//
//  FIX 1 — Hooks inside try/catch (Rules of Hooks violation):
//    useGLTF, useLoader, useFBX were all called inside a try/catch block.
//    React hooks must be called unconditionally at the top level.
//    Fix: split into GLBModel, OBJModel, FBXModel components — each
//    calls exactly one hook unconditionally at its top level.
//    ErrorBoundary wraps each to catch load failures cleanly.
//
//  FIX 2 — Two OrbitControls with makeDefault fighting each other:
//    AutoRotateControls and SyncedControls both mounted <OrbitControls makeDefault />.
//    They cancelled each other's camera updates every frame.
//    Fix: single <OrbitControls> instance with autoRotate prop passed down.
//    Auto-rotation uses drei's built-in autoRotate + autoRotateSpeed — no
//    separate component needed.
//
//  FIX 3 — THREE used but never imported:
//    new THREE.MeshStandardMaterial() in useMemo threw ReferenceError.
//    Fix: import * as THREE from 'three'.
//
//  All original features preserved: live session sync, health alerts,
//  intensity slider, diagnostics overlay, client mode, auto-rotate toggle.

import React, {
  Suspense, useEffect, useState, useRef, useMemo, Component,
} from 'react';
import { Canvas, useFrame, useThree, useLoader } from '@react-three/fiber';
import {
  OrbitControls, Stage, useGLTF, useFBX, Html, useProgress,
} from '@react-three/drei';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import * as THREE from 'three';                    // FIX 3
import { useNavigate, useLocation } from 'react-router-dom';
import {
  doc, onSnapshot, updateDoc, setDoc, deleteDoc, serverTimestamp, getDoc,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import toast from 'react-hot-toast';

const GOLD = '#B8860B';

// ─────────────────────────────────────────────────────────────────────────────
//  FIX 1 — SEPARATE MODEL COMPONENTS (each hook called unconditionally)
// ─────────────────────────────────────────────────────────────────────────────

function GLBModel({ url, type, intensity, onError }) {
  // useGLTF called unconditionally at top level — Rules of Hooks compliant
  const { scene: raw } = useGLTF(url);
  const scene = useMemo(() => {
    if (!raw) return null;
    const clone = raw.clone(true);
    clone.traverse((child) => {
      if (child.isMesh) {
        child.castShadow    = true;
        child.receiveShadow = true;
        if (type === 'head') {
          child.material            = new THREE.MeshStandardMaterial();  // FIX 3
          child.material.color.set('#A9A9A9');
          child.material.roughness  = 0.5;
          child.material.metalness  = 0.2;
        }
        if (type === 'hair' && child.material) {
          child.material            = child.material.clone();
          child.material.roughness  = 0.4;
          child.material.metalness  = 0.1;
          child.material.transparent = true;
          child.material.opacity    = 0.95;
        }
      }
    });
    return clone;
  }, [raw, type]);
  if (!scene) return null;
  const scale = type === 'hair' ? 1.51 + (intensity * 0.04) : 1.5;
  return <primitive object={scene} scale={scale} />;
}

function OBJModel({ url, type, intensity }) {
  // useLoader called unconditionally
  const obj = useLoader(OBJLoader, url);
  const scene = useMemo(() => {
    if (!obj) return null;
    const clone = obj.clone();
    const mat = new THREE.MeshStandardMaterial({  // FIX 3
      color: '#A9A9A9', roughness: 0.5, metalness: 0.2,
    });
    clone.traverse((child) => {
      if (child.isMesh) { child.material = mat; child.castShadow = true; }
    });
    return clone;
  }, [obj]);
  if (!scene) return null;
  const scale = type === 'hair' ? 1.51 + (intensity * 0.04) : 1.5;
  return <primitive object={scene} scale={scale} />;
}

function FBXModel({ url, type, intensity }) {
  // useFBX called unconditionally
  const fbx = useFBX(url);
  const scale = type === 'hair' ? 1.51 + (intensity * 0.04) : 1.5;
  if (!fbx) return null;
  return <primitive object={fbx} scale={scale} />;
}

// ─────────────────────────────────────────────────────────────────────────────
//  MODEL ROUTER — picks the right component based on file extension.
//  FIX 1: no hooks inside conditionals — each sub-component owns its hook.
// ─────────────────────────────────────────────────────────────────────────────
function Model({ url, type, intensity = 0.5, onError }) {
  if (!url) return null;
  const lower = url.toLowerCase();
  if (lower.endsWith('.obj')) return <OBJModel url={url} type={type} intensity={intensity} />;
  if (lower.endsWith('.fbx')) return <FBXModel url={url} type={type} intensity={intensity} />;
  return <GLBModel url={url} type={type} intensity={intensity} onError={onError} />;
}

// ─────────────────────────────────────────────────────────────────────────────
//  ERROR BOUNDARY for 3D models
// ─────────────────────────────────────────────────────────────────────────────
class ModelErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) { if (this.props.onError) this.props.onError(err); }
  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
//  FIX 2 — SINGLE OrbitControls component
//  Auto-rotation uses drei's built-in autoRotate prop.
//  SyncedControls also uses a single ref to the same OrbitControls.
// ─────────────────────────────────────────────────────────────────────────────
function SceneControls({ autoRotate, sessionId, isMaster }) {
  const controlsRef = useRef();
  const lastUpdate  = useRef(0);
  const { invalidate } = useThree();

  // Live session: receiver syncs camera from Firestore
  useEffect(() => {
    if (!sessionId || isMaster) return;
    const unsub = onSnapshot(doc(db, 'live_sessions', sessionId), (snap) => {
      if (snap.exists() && controlsRef.current) {
        const { camera } = snap.data();
        if (camera) {
          controlsRef.current.object.position.set(camera.x, camera.y, camera.z);
          controlsRef.current.target.set(camera.tx, camera.ty, camera.tz);
          invalidate();
        }
      }
    });
    return () => unsub();
  }, [sessionId, isMaster, invalidate]);

  // Live session: master broadcasts camera to Firestore
  useFrame(() => {
    const now = Date.now();
    if (isMaster && controlsRef.current && sessionId && now - lastUpdate.current > 500) {
      const pos = controlsRef.current.object.position;
      const tar = controlsRef.current.target;
      updateDoc(doc(db, 'live_sessions', sessionId), {
        camera: { x: pos.x, y: pos.y, z: pos.z, tx: tar.x, ty: tar.y, tz: tar.z },
        lastUpdated: serverTimestamp(),
      }).catch(() => {});
      lastUpdate.current = now;
    }
  });

  return (
    <OrbitControls
      ref={controlsRef}
      makeDefault
      enablePan={false}
      minDistance={2.5}
      maxDistance={8}
      autoRotate={autoRotate}          // FIX 2: built-in prop, no extra component
      autoRotateSpeed={0.6}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  LOADING INDICATOR
// ─────────────────────────────────────────────────────────────────────────────
function CustomLoader() {
  const { progress } = useProgress();
  return (
    <Html center>
      <div style={{ textAlign: 'center', width: '250px' }}>
        <div style={{ color: GOLD, fontWeight: '900', fontSize: '0.7rem', letterSpacing: '3px', marginBottom: '10px' }}>
          LOADING BIOMETRIC MODEL
        </div>
        <div style={{ width: '100%', height: '2px', background: 'rgba(184,134,11,0.2)' }}>
          <div style={{ width: `${progress}%`, height: '100%', background: GOLD, transition: '0.3s' }} />
        </div>
      </div>
    </Html>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN VIEWER
// ─────────────────────────────────────────────────────────────────────────────
export default function ThreeViewer({ modelUrl: propModelUrl, headUrl: propHeadUrl, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [modelUrl,        setModelUrl]        = useState(propModelUrl  || null);
  const [headUrl,         setHeadUrl]         = useState(propHeadUrl   || null);
  const [intensity,       setIntensity]       = useState(0.5);
  const [isMaster,        setIsMaster]        = useState(false);
  const [sessionId,       setSessionId]       = useState(null);
  const [healthAlert,     setHealthAlert]     = useState(null);
  const [clientName,      setClientName]      = useState('');
  const [modelLoadError,  setModelLoadError]  = useState(false);
  const [autoRotate,      setAutoRotate]      = useState(true);

  const targetClientUid = location.state?.targetClientUid;

  // Fetch biometric data
  useEffect(() => {
    const fetchBiometrics = async () => {
      const uid = targetClientUid || auth.currentUser?.uid;
      if (!uid) return;
      const snap = await getDoc(doc(db, 'users', uid));
      if (snap.exists()) {
        const d = snap.data();
        setClientName(d.displayName || 'Client');
        const scanUrl = d.headScan || d.initialRenderUrl || d.lastScanUrl;
        if (scanUrl && !propHeadUrl) setHeadUrl(scanUrl);
        if (d.healthFlags) setHealthAlert(d.healthFlags);
      }
    };
    fetchBiometrics();
  }, [targetClientUid, propHeadUrl]);

  // Role check + live session
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    getDoc(doc(db, 'users', user.uid)).then(snap => {
      if (snap.exists()) {
        const role = snap.data().role;
        if (role === 'stylist' || role === 'barber') setIsMaster(true);
      }
    });

    const params          = new URLSearchParams(window.location.search);
    const existingSession = params.get('session');
    if (existingSession) {
      setSessionId(existingSession);
      const unsub = onSnapshot(doc(db, 'live_sessions', existingSession), (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setIntensity(data.intensity || 0.5);
          if (data.styleUrl) setModelUrl(data.styleUrl);
        } else {
          setSessionId(null);
          toast.error('Live session ended.');
        }
      });
      return () => unsub();
    }
  }, []);

  const handleModelError = (err) => {
    console.error('3D Model load failed:', err);
    setModelLoadError(true);
    toast.error('Failed to load 3D cranial model');
  };

  const retryModelLoad = () => {
    setModelLoadError(false);
    const h = headUrl, m = modelUrl;
    setHeadUrl(null); setModelUrl(null);
    setTimeout(() => { setHeadUrl(h); setModelUrl(m); }, 100);
  };

  const startLiveSession = async () => {
    if (!targetClientUid) return toast.error('Select a client first.');
    const sId        = `session_${auth.currentUser.uid}_${Date.now()}`;
    const inviteLink = `${window.location.origin}${window.location.pathname}?session=${sId}`;
    try {
      await setDoc(doc(db, 'live_sessions', sId), {
        masterId:    auth.currentUser.uid,
        receiverId:  targetClientUid,
        intensity,
        styleUrl:    modelUrl,
        createdAt:   serverTimestamp(),
        lastUpdated: serverTimestamp(),
      });
      setSessionId(sId);
      navigator.clipboard.writeText(inviteLink);
      toast.success('Consultation Link Copied!');
    } catch { toast.error('Session failed.'); }
  };

  const handleIntensityChange = (val) => {
    setIntensity(val);
    if (isMaster && sessionId) updateDoc(doc(db, 'live_sessions', sessionId), { intensity: val });
  };

  return (
    <div style={viewerContainer}>

      {/* HEADER */}
      <div style={overlayHeader}>
        <div>
          <h2 style={{ color: GOLD, margin: 0, letterSpacing: '4px', fontSize: '1rem' }}>
            {sessionId ? 'LIVE CONSULTATION' : 'BIOMETRIC VIEW'}
          </h2>
          <p style={{ color: 'white', margin: 0, fontSize: '0.6rem', opacity: 0.7 }}>
            {isMaster ? `CLIENT: ${clientName.toUpperCase()}` : '3D CRANIAL SOLID PROJECTION'}
          </p>
        </div>

        {healthAlert && (
          <div style={alertBadge}>⚠️ HEALTH ALERT: {healthAlert.toUpperCase()}</div>
        )}

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {isMaster && !sessionId && (
            <button onClick={startLiveSession} style={liveBtn}>START LIVE SYNC</button>
          )}
          {isMaster && sessionId && (
            <button onClick={() => deleteDoc(doc(db, 'live_sessions', sessionId))} style={endBtn}>
              END SESSION
            </button>
          )}
          <button
            onClick={() => setAutoRotate(r => !r)}
            style={{ ...autoRotateBtn, background: autoRotate ? '#00cc66' : 'rgba(255,255,255,0.1)', color: autoRotate ? '#000' : '#fff' }}>
            {autoRotate ? '⏹ STOP ROTATION' : '▶ AUTO ROTATE'}
          </button>
          <button onClick={onClose || (() => navigate(-1))} style={closeBtn}>EXIT</button>
        </div>
      </div>

      {/* CANVAS */}
      <Canvas shadows camera={{ position: [0, 0, 5], fov: 45 }}>
        <color attach="background" args={['#01050a']} />
        <ambientLight intensity={0.4} />
        <pointLight position={[10, 10, 10]}   intensity={1.2} color="#ffffff" />
        <pointLight position={[-8, 5, -10]}   intensity={0.8} color={GOLD} />

        <Suspense fallback={<CustomLoader />}>
          <Stage environment="city" intensity={0.6} adjustCamera={1.1}>
            <group>
              {/* FIX 1: ModelErrorBoundary wraps each Model cleanly */}
              {headUrl && !modelLoadError && (
                <ModelErrorBoundary onError={handleModelError}>
                  <Model url={headUrl}  type="head" intensity={intensity} onError={handleModelError} />
                </ModelErrorBoundary>
              )}
              {modelUrl && !modelLoadError && (
                <ModelErrorBoundary onError={handleModelError}>
                  <Model url={modelUrl} type="hair" intensity={intensity} onError={handleModelError} />
                </ModelErrorBoundary>
              )}
            </group>
          </Stage>

          {/* FIX 2: single SceneControls — no two OrbitControls fighting */}
          <SceneControls
            autoRotate={autoRotate}
            sessionId={sessionId}
            isMaster={isMaster}
          />
        </Suspense>
      </Canvas>

      {/* ERROR OVERLAY */}
      {modelLoadError && (
        <div style={errorOverlay}>
          <div style={errorContent}>
            <p style={{ color: '#ffaa00', marginBottom: '15px' }}>Failed to load 3D Cranial Solid</p>
            <button onClick={retryModelLoad} style={retryButton}>Retry Loading Model</button>
          </div>
        </div>
      )}

      {/* CONTROLS PANEL */}
      <div style={controlsPanel}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
          <span style={{ color: GOLD, fontSize: '0.7rem', fontWeight: 'bold' }}>HAIRSTYLE DENSITY</span>
          <span style={{ color: 'white', fontSize: '0.7rem' }}>{Math.round(intensity * 100)}%</span>
        </div>
        <input
          type="range" min="0" max="1" step="0.01"
          disabled={!isMaster && !!sessionId}
          value={intensity}
          onChange={e => handleIntensityChange(parseFloat(e.target.value))}
          style={sliderStyle}
        />
        <div style={controlsHint}>BIOMETRIC CRANIAL MAP • PRECISION VIEW</div>
      </div>
    </div>
  );
}

// ── STYLES ────────────────────────────────────────────────────────────────────
const viewerContainer = { position:'fixed', inset:0, background:'#000', zIndex:1000, display:'flex', flexDirection:'column' };
const overlayHeader   = { position:'absolute', top:0, width:'100%', padding:'30px', display:'flex', justifyContent:'space-between', alignItems:'center', zIndex:10, background:'linear-gradient(to bottom, rgba(0,0,0,0.9), transparent)', boxSizing:'border-box' };
const alertBadge      = { background:'rgba(255,68,68,0.2)', color:'#ff4444', border:'1px solid #ff4444', padding:'5px 15px', borderRadius:'5px', fontSize:'0.6rem', fontWeight:'bold' };
const closeBtn        = { background:'none', border:`1px solid ${GOLD}`, color:GOLD, padding:'8px 20px', borderRadius:'50px', fontWeight:'bold', cursor:'pointer', fontSize:'0.6rem' };
const liveBtn         = { background:GOLD, border:'none', color:'black', padding:'8px 20px', borderRadius:'50px', fontWeight:'bold', cursor:'pointer', fontSize:'0.6rem' };
const endBtn          = { background:'#ff4444', border:'none', color:'white', padding:'8px 20px', borderRadius:'50px', fontWeight:'bold', cursor:'pointer', fontSize:'0.6rem' };
const autoRotateBtn   = { border:'none', padding:'8px 20px', borderRadius:'50px', fontWeight:'bold', cursor:'pointer', fontSize:'0.6rem', transition:'all 0.2s' };
const controlsPanel   = { position:'absolute', bottom:0, width:'100%', padding:'40px 30px', background:'linear-gradient(to top, #050a0f 80%, transparent)', zIndex:10, boxSizing:'border-box' };
const sliderStyle     = { width:'100%', accentColor:GOLD, cursor:'pointer' };
const controlsHint    = { textAlign:'center', color:'white', opacity:0.3, fontSize:'0.5rem', letterSpacing:'2px', marginTop:'20px' };
const errorOverlay    = { position:'absolute', inset:0, background:'rgba(0,0,0,0.85)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:20 };
const errorContent    = { textAlign:'center', padding:'30px', background:'#111', borderRadius:'16px', border:`2px solid ${GOLD}` };
const retryButton     = { background:'#00ff88', color:'#000', border:'none', padding:'14px 28px', borderRadius:'50px', fontWeight:'900', cursor:'pointer', fontSize:'1rem' };
